// MediaGalleryModal — Phase 19 admin-shared image picker (2026-05-20).
//
// Two tabs:
//   1. Library — grid of pre-existing media_assets filtered by
//      intended_use. Click to pick.
//   2. Upload  — drag-drop / file-picker. POSTs to /api/admin/media
//      with intended_use, which dedups via SHA-256 and returns the
//      (possibly-existing) asset row. The new asset is auto-selected.
//
// Used by:
//   • OfferArtworkSection — intended_use='offer_banner'
//   • AdminReferralSettings — intended_use='referral_card'
//
// Surface contract: the parent passes `onPick(asset)` and `onClose()`.
// `asset.url` is the canonical URL to persist on the parent's domain
// model (offers.image_url, platform_config.referral_card_image_url).

import { useEffect, useState } from 'react';
import Icon from '../../components/Icon';
import { adminApi } from '../lib/adminApi';
import type { ApiError } from '../../lib/api';
import { compressForUpload, CompressPreset } from '../../lib/imageCompress';

export type MediaIntendedUse =
  | 'offer_banner'
  | 'referral_card'
  | 'app_icon';

export type MediaAsset = {
  id: number;
  intended_use: MediaIntendedUse | string;
  url: string;
  thumb_url: string | null;
  mime: string;
  bytes: number;
  width: number | null;
  height: number | null;
  sha256: string;
  original_name: string | null;
  uploaded_by_admin_id: number | null;
  archived_at: string | null;
  created_at: string;
};

type ListResponse = {
  rows: MediaAsset[];
  total: number;
  limit: number;
  offset: number;
};

type UploadResponse = {
  asset: MediaAsset;
  dedup_hit: boolean;
};

type Props = {
  intendedUse: MediaIntendedUse;
  /** Called when admin picks an asset. Parent typically persists
   *  asset.url then closes the modal. */
  onPick: (asset: MediaAsset) => void;
  onClose: () => void;
  /** Override default modal title (e.g. "Choose Offer Banner"). */
  title?: string;
  /** MIME whitelist for the upload tab. Defaults match the backend
   *  multer instance: PNG + JPEG + WebP. */
  acceptMimes?: string;
};

export default function MediaGalleryModal({
  intendedUse, onPick, onClose, title, acceptMimes = 'image/png,image/jpeg,image/webp',
}: Props) {
  const [tab, setTab] = useState<'library' | 'upload'>('library');
  const [data, setData] = useState<ListResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // 2026-05-25 — delete-from-library. A two-step flow: the trash button
  // on a tile only OPENS a confirmation dialog; nothing is removed until
  // the admin confirms there. The backend DELETE soft-archives the row
  // (archived_at) so it leaves the gallery but the file is retained.
  const [confirmDelete, setConfirmDelete] = useState<MediaAsset | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function doDelete(asset: MediaAsset) {
    setDeleting(true); setErr(null);
    try {
      await adminApi.del(`/admin/media/${asset.id}`);
      // Optimistically drop it from the visible grid + count.
      setData((d) => d
        ? { ...d, rows: d.rows.filter((r) => r.id !== asset.id), total: Math.max(0, d.total - 1) }
        : d);
      setConfirmDelete(null);
    } catch (e) {
      setErr((e as ApiError).message || 'Could not delete this image.');
      setConfirmDelete(null);
    } finally { setDeleting(false); }
  }

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Load library on mount + whenever the tab switches back to library.
  useEffect(() => {
    if (tab !== 'library') return;
    let cancelled = false;
    setErr(null);
    (async () => {
      try {
        const r = await adminApi.get<ListResponse>(
          `/admin/media?intended_use=${encodeURIComponent(intendedUse)}&limit=100`
        );
        if (!cancelled) setData(r);
      } catch (e) {
        if (!cancelled) setErr((e as ApiError).message || 'Failed to load library.');
      }
    })();
    return () => { cancelled = true; };
  }, [tab, intendedUse]);

  async function handleUpload(rawFile: File) {
    setBusy(true); setErr(null);
    try {
      // 2026-05-20 — client-side compression in front of the server's
      // sharp pipeline. Defense-in-depth: server still strips metadata
      // + normalises; this is purely a bandwidth/latency win. Maps
      // intended_use → preset so PNG-with-alpha (offer_banner) keeps
      // its transparency and proofs stay sharp at 1920px.
      const preset = intendedUseToPreset(intendedUse);
      const file = await compressForUpload(rawFile, { preset });
      const fd = new FormData();
      fd.append('image', file, file.name || 'media');
      fd.append('intended_use', intendedUse);
      const r = await adminApi.postForm<UploadResponse>('/admin/media', fd);
      // Auto-pick after upload — admin almost always wants to use the
      // thing they just uploaded.
      onPick(r.asset);
    } catch (e) {
      setErr((e as ApiError).message || 'Upload failed.');
    } finally { setBusy(false); }
  }

  return (
    <>
    <div
      className="fixed inset-0 z-[90] grid place-items-center
                 bg-black/65 backdrop-blur-md p-3 sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative w-full max-w-4xl max-h-[92dvh] flex flex-col
                   rounded-3xl overflow-hidden ring-1 ring-arena-200/60 dark:ring-white/10
                   bg-white dark:bg-arena-900 shadow-[0_20px_60px_-25px_rgba(0,0,0,0.45)]"
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-arena-200/60 dark:border-white/10">
          <span className="grid place-items-center w-10 h-10 rounded-xl
                           bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60
                           dark:bg-emerald-400/15 dark:text-emerald-300 dark:ring-emerald-400/30">
            <Icon name="photo_library" size={22} filled />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-display font-bold text-base sm:text-lg text-arena-900 dark:text-white">
              {title || titleFor(intendedUse)}
            </h2>
            <p className="text-[11px] text-arena-600 dark:text-neon-200/70 truncate">
              Pick from the library or upload a new image. Re-uploads
              of the same file are deduplicated.
            </p>
          </div>
          <button onClick={onClose} aria-label="Close"
                  className="text-2xl leading-none text-arena-500 hover:text-arena-900
                             dark:text-neon-200/55 dark:hover:text-white">×</button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-4 pt-3">
          <TabBtn active={tab === 'library'} onClick={() => setTab('library')}>
            <Icon name="grid_view" size={14} /> Library
            {data && (
              <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold
                               bg-arena-100 text-arena-700 dark:bg-white/10 dark:text-white/70">
                {data.total}
              </span>
            )}
          </TabBtn>
          <TabBtn active={tab === 'upload'} onClick={() => setTab('upload')}>
            <Icon name="upload" size={14} /> Upload new
          </TabBtn>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 pb-4 pt-3">
          {err && (
            <div className="mb-3 rounded-lg bg-rose-500/10 ring-1 ring-inset ring-rose-400/40
                            text-rose-700 dark:text-rose-200 text-xs px-3 py-2">
              {err}
            </div>
          )}

          {tab === 'library' && (
            <LibraryGrid
              data={data}
              onPick={onPick}
              onRequestDelete={(a) => setConfirmDelete(a)}
            />
          )}

          {tab === 'upload' && (
            <UploadPane
              busy={busy}
              acceptMimes={acceptMimes}
              onFile={handleUpload}
            />
          )}
        </div>
      </div>
    </div>

    {/* Confirmation dialog — sits above the gallery. A trash click only
        opens this; the image is removed only on explicit confirm. */}
    {confirmDelete && (
      <ConfirmDeleteDialog
        asset={confirmDelete}
        busy={deleting}
        onCancel={() => { if (!deleting) setConfirmDelete(null); }}
        onConfirm={() => doDelete(confirmDelete)}
      />
    )}
    </>
  );
}

function titleFor(use: MediaIntendedUse): string {
  switch (use) {
    case 'offer_banner':  return 'Offer Artwork — Media Library';
    case 'referral_card': return 'Referral Card icon — Media Library';
    case 'app_icon':      return 'App Icon — Media Library';
    default:              return 'Media Library';
  }
}

function intendedUseToPreset(use: MediaIntendedUse): CompressPreset {
  switch (use) {
    case 'offer_banner':  return 'offer_banner';
    case 'referral_card': return 'referral_card';
    case 'app_icon':      return 'app_icon';
    default:              return 'generic';
  }
}

function TabBtn({
  active, onClick, children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg
                  text-[12px] font-bold uppercase tracking-wider transition
                  ${active
                    ? 'bg-emerald-600 text-white shadow-[0_2px_10px_rgba(16,185,129,0.3)]'
                    : 'text-arena-700 dark:text-neon-200/80 hover:bg-arena-100 dark:hover:bg-white/[0.06]'}`}
    >
      {children}
    </button>
  );
}

function LibraryGrid({
  data, onPick, onRequestDelete,
}: {
  data: ListResponse | null;
  onPick: (a: MediaAsset) => void;
  onRequestDelete: (a: MediaAsset) => void;
}) {
  if (!data) {
    return (
      <div className="grid place-items-center py-12 text-arena-600 dark:text-neon-200/70 text-sm">
        <Icon name="progress_activity" size={24} className="animate-spin" />
        <p className="mt-2">Loading library…</p>
      </div>
    );
  }
  if (data.rows.length === 0) {
    return (
      <div className="grid place-items-center py-12 text-arena-600 dark:text-neon-200/70 text-sm text-center">
        <Icon name="inbox" size={36} className="opacity-60 mb-2" />
        <p>No images in the library yet for this slot.</p>
        <p className="text-[11px] text-arena-500 dark:text-neon-200/50 mt-1">
          Switch to the Upload tab to add one.
        </p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {data.rows.map((a) => (
        <div
          key={a.id}
          className="group relative rounded-xl overflow-hidden
                     ring-1 ring-arena-200/60 dark:ring-white/10
                     hover:ring-emerald-400 dark:hover:ring-emerald-400
                     bg-arena-50 dark:bg-arena-800/40 transition"
        >
          {/* Pick area — the whole tile is the pick target. */}
          <button
            type="button"
            onClick={() => onPick(a)}
            className="block w-full text-left focus:outline-none
                       focus:ring-2 focus:ring-emerald-500 rounded-xl"
          >
            <div className="aspect-square grid place-items-center
                            bg-gradient-to-br from-[#0a1626] via-[#0e1d33] to-[#1a0f08]">
              <img
                src={a.thumb_url || a.url}
                alt={a.original_name || `Asset #${a.id}`}
                loading="lazy"
                className="w-[78%] h-[78%] object-contain
                           drop-shadow-[0_2px_4px_rgba(0,0,0,0.25)]"
              />
            </div>
            <div className="px-2 py-1.5 text-[10px] text-arena-600 dark:text-neon-200/55 truncate">
              {(a.original_name || `#${a.id}`)} · {(a.bytes / 1024).toFixed(0)} KB
              {a.width && a.height && ` · ${a.width}×${a.height}`}
            </div>
            <div className="absolute inset-0 grid place-items-center
                            bg-emerald-500/0 group-hover:bg-emerald-500/15
                            transition pointer-events-none">
              <span className="opacity-0 group-hover:opacity-100 transition
                               px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider
                               text-white bg-emerald-600 shadow-[0_2px_10px_rgba(16,185,129,0.45)]">
                Pick
              </span>
            </div>
          </button>

          {/* Delete — sibling of the pick button (never nested). Opens
              the confirmation dialog; does NOT delete on its own. Kept
              always-visible (subtle) so it works on touch as well as
              hover. */}
          <button
            type="button"
            title="Delete from library"
            aria-label={`Delete ${a.original_name || `asset #${a.id}`} from library`}
            onClick={(e) => { e.stopPropagation(); onRequestDelete(a); }}
            className="absolute top-1.5 right-1.5 z-10 grid place-items-center
                       w-7 h-7 rounded-full
                       bg-black/45 text-white backdrop-blur-sm
                       ring-1 ring-white/20
                       hover:bg-rose-600 hover:ring-rose-300/60
                       focus:outline-none focus:ring-2 focus:ring-rose-400
                       transition active:scale-95"
          >
            <Icon name="delete" size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}

function ConfirmDeleteDialog({
  asset, busy, onConfirm, onCancel,
}: {
  asset: MediaAsset;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center
                 bg-black/70 backdrop-blur-md p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
      role="dialog" aria-modal="true"
    >
      <div className="w-full max-w-sm rounded-2xl overflow-hidden
                      bg-white dark:bg-arena-900
                      ring-1 ring-arena-200/60 dark:ring-white/10
                      shadow-[0_20px_60px_-25px_rgba(0,0,0,0.55)]">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-arena-200/60 dark:border-white/10">
          <span className="grid place-items-center w-10 h-10 rounded-xl shrink-0
                           bg-rose-50 text-rose-600 ring-1 ring-rose-200/60
                           dark:bg-rose-400/15 dark:text-rose-300 dark:ring-rose-400/30">
            <Icon name="delete" size={20} filled />
          </span>
          <h3 className="font-display font-bold text-base text-arena-900 dark:text-white">
            Delete from library?
          </h3>
        </div>

        <div className="p-5">
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0
                            ring-1 ring-arena-200/60 dark:ring-white/10
                            bg-gradient-to-br from-[#0a1626] via-[#0e1d33] to-[#1a0f08]
                            grid place-items-center">
              <img src={asset.thumb_url || asset.url} alt=""
                   className="w-[78%] h-[78%] object-contain" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-[13px] text-arena-900 dark:text-white truncate">
                {asset.original_name || `Asset #${asset.id}`}
              </div>
              <p className="mt-1 text-[12px] text-arena-600 dark:text-neon-200/70 leading-snug">
                This removes it from the library so it can no longer be
                picked. Images already attached to a saved offer keep
                showing — this only affects the picker.
              </p>
            </div>
          </div>
        </div>

        <div className="px-5 pb-5 flex gap-2">
          <button
            type="button" onClick={onCancel} disabled={busy}
            className="flex-1 inline-flex items-center justify-center gap-1.5
                       px-4 py-2.5 rounded-full text-[13px] font-semibold
                       bg-arena-100/70 hover:bg-arena-200/70
                       dark:bg-white/[0.06] dark:hover:bg-white/[0.1]
                       text-arena-700 dark:text-neon-100/85
                       ring-1 ring-inset ring-arena-200/60 dark:ring-white/10
                       transition active:scale-95 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button" onClick={onConfirm} disabled={busy}
            className="flex-1 inline-flex items-center justify-center gap-1.5
                       px-4 py-2.5 rounded-full text-[13px] font-bold text-white
                       bg-rose-600 hover:bg-rose-500 active:bg-rose-700
                       shadow-[0_2px_10px_rgba(244,63,94,0.4)]
                       transition active:scale-95 disabled:opacity-60"
          >
            <Icon name={busy ? 'progress_activity' : 'delete'} size={15}
                  className={busy ? 'animate-spin' : ''} />
            {busy ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

function UploadPane({
  busy, acceptMimes, onFile,
}: {
  busy: boolean;
  acceptMimes: string;
  onFile: (file: File) => void;
}) {
  const [dragOver, setDragOver] = useState(false);

  function pick() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = acceptMimes;
    input.onchange = () => {
      const f = input.files?.[0];
      if (f) onFile(f);
    };
    input.click();
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault(); setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
      }}
      onClick={pick}
      role="button"
      tabIndex={0}
      className={`rounded-2xl border-2 border-dashed
                  transition cursor-pointer
                  min-h-[300px] grid place-items-center p-6
                  ${dragOver
                    ? 'border-emerald-400 bg-emerald-500/5'
                    : 'border-arena-300 dark:border-white/15 hover:border-emerald-500/60 bg-arena-50/40 dark:bg-arena-800/40'}`}
    >
      <div className="text-center">
        {busy ? (
          <>
            <Icon name="progress_activity" size={32} className="animate-spin text-emerald-600 mx-auto mb-3" />
            <p className="text-sm text-arena-700 dark:text-neon-200/80">Uploading + deduplicating…</p>
          </>
        ) : (
          <>
            <div className="mx-auto w-14 h-14 rounded-2xl grid place-items-center mb-3
                            bg-emerald-500/10 text-emerald-700 dark:text-emerald-300
                            ring-1 ring-emerald-400/30">
              <Icon name="cloud_upload" size={28} filled />
            </div>
            <p className="font-bold text-sm text-arena-900 dark:text-white">
              Drop an image here or click to choose
            </p>
            <p className="text-[11px] text-arena-600 dark:text-neon-200/55 mt-1 max-w-[320px] mx-auto">
              PNG, JPEG, or WebP · up to 6 MB · client-side compression
              applies first, then server stripping + normalisation.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
